import { ArrowDownUp } from 'lucide-react';
import { FilterSelect, IconButton, Input } from '@wundo/ui';
import type { OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import { m } from '@/paraglide/messages.js';
import type { TeamSort } from './sortColleagues.ts';
import { inTeamFilter, type TeamFilter } from './teamFilter.ts';

export interface TeamToolbarProps {
  /** The colleagues on the page, which the filter options count. */
  colleagues: OneOnOneColleagueItem[];
  now: Date;
  search: string;
  onSearchChange: (search: string) => void;
  filter: TeamFilter;
  onFilterChange: (filter: TeamFilter) => void;
  sort: TeamSort;
  onSortChange: (sort: TeamSort) => void;
}

/** Search, filter and sort for the Team page's list. */
export function TeamToolbar({
  colleagues,
  now,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  sort,
  onSortChange,
}: TeamToolbarProps) {
  const countOf = (option: TeamFilter) =>
    colleagues.filter((colleague) => inTeamFilter(colleague, option, now)).length;

  return (
    <div className='mt-6 flex flex-wrap items-center gap-x-5 gap-y-3'>
      <Input
        type='search'
        className='min-w-60 flex-1'
        aria-label={m.team_search_label()}
        placeholder={m.team_search_placeholder()}
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
      />
      <div className='flex flex-wrap items-center gap-x-5 gap-y-3 max-mobile:gap-x-3'>
        <FilterSelect
          label={m.team_filter_label()}
          options={
            [
              {
                value: 'all',
                label: m.team_filter_all({ count: colleagues.length }),
              },
              {
                value: 'memory',
                label: m.team_filter_memory({ count: countOf('memory') }),
              },
              {
                value: 'no_memory',
                label: m.team_filter_no_memory({ count: countOf('no_memory') }),
              },
              {
                value: 'nothing_planned',
                label: m.team_filter_nothing_planned({
                  count: countOf('nothing_planned'),
                }),
              },
              {
                value: 'action',
                label: m.team_filter_action({ count: countOf('action') }),
              },
              {
                value: 'planned',
                label: m.team_filter_planned({ count: countOf('planned') }),
              },
              { value: 'hidden', label: m.team_filter_hidden() },
            ] as const
          }
          value={filter}
          onValueChange={onFilterChange}
        />
        <div className='flex items-center gap-1'>
          <FilterSelect
            label={m.team_sort_label()}
            options={
              [
                { value: 'next', label: m.team_next_label() },
                { value: 'name', label: m.team_sort_name() },
                { value: 'support', label: m.team_support_title() },
              ] as const
            }
            value={sort.key}
            onValueChange={(key) => onSortChange({ key, direction: 'asc' })}
          />
          <IconButton
            variant='quiet'
            size='sm'
            aria-label={m.team_sort_reverse()}
            aria-pressed={sort.direction === 'desc'}
            onClick={() =>
              onSortChange({
                ...sort,
                direction: sort.direction === 'asc' ? 'desc' : 'asc',
              })
            }
            className='text-ink-muted aria-pressed:text-ink'
          >
            <ArrowDownUp className='size-3.5' aria-hidden='true' />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
